import { useEffect, useRef, useState } from "react";
import { Heart, Plus } from "lucide-react";
import EmojiPicker, {
  EmojiStyle,
  Theme,
  type EmojiClickData,
} from "emoji-picker-react";
import { Button } from "@themes/pollinator/ui/Button";
import { Dropdown } from "@themes/pollinator/ui/Dropdown";
import type { ContainerBreakpoints } from "../../videoMeeting.types";

const QUICK_REACTIONS = ["❤️", "👍", "👎", "😂", "😮", "🎉", "🐝"];

export type Props = {
  containerBreakpoints: ContainerBreakpoints;
  isThrottled: boolean;
  onSendReaction: (emoji: string) => void;
  /**
   * When true, the popover opens with the full emoji picker instead of the
   * quick-bar. Used by Storybook to iterate on picker styling without
   * click-through. Not wired into production rendering.
   */
  defaultShowFullPicker?: boolean;
};

/**
 * Reactions control: a button that opens a popover with 7 quick emoji
 * reactions and a `+` button that expands into a full emoji picker.
 *
 * When `isThrottled` is true (1s cooldown after a send), the trigger button
 * and all emoji options are visually disabled so repeat clicks are visibly
 * rejected rather than silently dropped.
 */
export function VideoMeetingControlsReactions({
  containerBreakpoints,
  isThrottled,
  onSendReaction,
  defaultShowFullPicker = false,
}: Props) {
  const [showFullPicker, setShowFullPicker] = useState(defaultShowFullPicker);
  // Ref to the trigger Button so handlePickerSelect can programmatically
  // dispatch a click on it — that toggles the Dropdown's internal isOpen
  // via floating-ui's useClick. Letting the emoji-selection click bubble
  // to the Dropdown's click-to-close handler doesn't reliably work because
  // emoji-picker-react attaches its click handler as a native listener,
  // and by the time the event reaches React's synthetic handlers the
  // parent may have re-rendered (setShowFullPicker(false)) and the picker
  // wrapper is no longer in the tree. An explicit close via the trigger
  // is the reliable path.
  const triggerButtonRef = useRef<HTMLButtonElement>(null);

  // Responsive picker width: 320px on desktop, shrunk to fit the viewport
  // (minus 16px for the Dropdown's shift() padding × 2 edges) on narrow
  // phones. Kept as a number so both the wrapper div and the EmojiPicker's
  // own width prop agree — passing "100%" to EmojiPicker doesn't propagate
  // reliably through its internal flex/grid layout.
  const [pickerWidth, setPickerWidth] = useState<number>(320);
  useEffect(() => {
    const update = () => {
      if (typeof window === "undefined") return;
      setPickerWidth(Math.min(320, Math.max(240, window.innerWidth - 16)));
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const handleQuickReaction = (emoji: string) => {
    if (isThrottled) return;
    onSendReaction(emoji);
    setShowFullPicker(false);
  };

  const handlePickerSelect = (data: EmojiClickData) => {
    if (isThrottled) return;
    onSendReaction(data.emoji);
    setShowFullPicker(false);
    // Close the popover by re-clicking the trigger (useClick toggles isOpen).
    triggerButtonRef.current?.click();
  };

  return (
    <Dropdown
      placement="top"
      triggerClassName="relative flex items-center"
      className="!bg-brand-900 !border-brand-800 !min-w-0 !max-w-none"
      data-testid="video-meeting-controls-reactions"
      trigger={
        <Button
          ref={triggerButtonRef}
          iconComponent={Heart}
          variant="ghost"
          theme="dark"
          visuallyDisabled={isThrottled}
          aria-label="Send a reaction"
        >
          {containerBreakpoints.isMd ? "React" : ""}
        </Button>
      }
    >
      {showFullPicker ? (
        // Stop all clicks from bubbling — the Dropdown's floating element
        // has an onClick that dismisses on any inside-click, which would
        // fire on every search/category/scroll click inside the picker.
        // The one click we DO want to close the Dropdown is emoji selection,
        // handled explicitly in handlePickerSelect by clicking the trigger
        // programmatically (see triggerButtonRef).
        //
        // Explicit width + height on the wrapper: the parent Dropdown uses
        // floating-ui's flip() middleware, which measures the popover at
        // open time to decide top vs bottom placement. Without a fixed-size
        // wrapper, the picker's internal layout isn't stable on first open
        // (lazy-load, async layout) and flip() ends up placing it below the
        // button where it gets cut off by the viewport.
        <div
          style={{ width: pickerWidth, height: 360 }}
          onClick={(e) => e.stopPropagation()}
          data-testid="video-meeting-controls-reactions-picker"
        >
          <EmojiPicker
            onEmojiClick={handlePickerSelect}
            theme={Theme.DARK}
            emojiStyle={EmojiStyle.NATIVE}
            skinTonesDisabled
            previewConfig={{ showPreview: false }}
            searchPlaceholder="Search"
            width={pickerWidth}
            height={360}
          />
        </div>
      ) : (
        <div
          className="flex flex-row items-center gap-1 p-1"
          data-testid="video-meeting-controls-reactions-quick-bar"
        >
          {QUICK_REACTIONS.map((emoji) => (
            <Button
              key={emoji}
              variant="ghost"
              theme="dark"
              disabled={isThrottled}
              aria-label={`React with ${emoji}`}
              typeClassName="text-xl md:text-3xl leading-none"
              onClick={() => handleQuickReaction(emoji)}
            >
              {emoji}
            </Button>
          ))}
          <Button
            iconComponent={Plus}
            variant="ghost"
            theme="dark"
            disabled={isThrottled}
            aria-label="Open full emoji picker"
            data-testid="video-meeting-controls-reactions-expand"
            // Stop propagation so the Dropdown's click-to-close handler
            // doesn't fire — we want to swap to picker view, not close.
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setShowFullPicker(true);
            }}
          />
        </div>
      )}
    </Dropdown>
  );
}

export default VideoMeetingControlsReactions;
